import { type KeyboardEvent, type TouchEvent, useCallback, useContext, useRef, useState } from 'react';
import { makeStyles } from '@mui/styles';
import { PreviousButton, NextButton, CloseButton } from './buttons';
import { imageViewerContext } from './ImageViewerContext';
import ImageViewerItem from './ImageViewerItem';

const TOUCH_PX_SENSIBILITY = 150;

const useStyles = makeStyles(() => ({
  container: {
    backgroundColor: 'rgb(0, 0, 0, 0.8)',
    height: '100%',
    left: 0,
    overflow: 'hidden',
    position: 'absolute',
    top: 0,
    width: '100%',
    zIndex: 1500,
  },
}), { name: 'ImageViewer' });

const ImageViewer = (): JSX.Element | null => {
  const classes = useStyles();

  const { currentViewedImage, imageDocuments, handleCurrentViewedImage } = useContext(imageViewerContext);

  const currentViewedImageIndex = imageDocuments.findIndex(
    (image) => image.id === currentViewedImage?.id,
  );

  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const handleTouchStart = useCallback(
    (e: TouchEvent) => setTouchStart(e.targetTouches[0].clientX),
  [setTouchStart]);

  const handleTouchMove = useCallback(
    (e: TouchEvent) => setTouchEnd(e.targetTouches[0].clientX),
  [setTouchEnd]);

  const hasNextImage =
    currentViewedImageIndex > -1 &&
    currentViewedImageIndex < imageDocuments.length - 1;

  const hasPreviousImage = currentViewedImageIndex >= 1;

  const handleTouchEnd = useCallback(() => {
    const touchDelta = touchStart - touchEnd;
    if (touchDelta > TOUCH_PX_SENSIBILITY && hasNextImage) {
      const nextImage = imageDocuments[currentViewedImageIndex + 1];
      handleCurrentViewedImage(nextImage);
    } else if (touchDelta < -TOUCH_PX_SENSIBILITY && hasPreviousImage) {
      const previousImage = imageDocuments[currentViewedImageIndex - 1];
      handleCurrentViewedImage(previousImage);
    }
  }, [currentViewedImageIndex, hasNextImage, hasPreviousImage, imageDocuments, touchEnd, touchStart, handleCurrentViewedImage]);

  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLImageElement>): void => {
    if (e.key === 'ArrowLeft' && hasPreviousImage) {
      const previousImage = imageDocuments[currentViewedImageIndex - 1];
      handleCurrentViewedImage(previousImage);
    } else if (e.key === 'ArrowRight' && hasNextImage) {
      const nextImage = imageDocuments[currentViewedImageIndex + 1];
      handleCurrentViewedImage(nextImage);
    } else if (e.key === 'Escape') {
      handleCurrentViewedImage(undefined);
    }
  }, [currentViewedImageIndex, hasNextImage, hasPreviousImage, imageDocuments, handleCurrentViewedImage]);

  const containerRef = useRef<HTMLDivElement>(null);

  if (!currentViewedImage) {
    return null;
  }

  return (
    <div
      data-testid='image-viewer'
      role='button'
      className={classes.container}
      tabIndex={0}
      ref={containerRef}
      onKeyDown={handleKeyDown}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      onTouchStart={handleTouchStart}
      // eslint-disable-next-line react/no-unknown-property
      onLoad={() => containerRef?.current?.focus()}
    >
      <CloseButton />

      <PreviousButton />

      {imageDocuments.map((image) => (
        <ImageViewerItem image={image} key={image.id} />
      ))}

      <NextButton />
    </div>
  )
};

export default ImageViewer;
